/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › Decisions (docs/CONTRACT-fleet-governance.md sections 7.4, 9.1 and 12.3): per app, how Gyro's writes were
 * decided: ran on their own, confirmed by the person, approved by an approver, or declined (cancelled, rejected, expired),
 * one ShareBar each with its count and share, and the auto-run share over the range. A row opens its audit rows
 * (`action.`, that app).
 */
import { useEffect, type ReactNode } from 'react';
import { ListChecks } from 'lucide-react';
import { ACTION_CHOICE, AUTO_RUN_CHOICE, shareOf, sparkValues, trendsApi, trendsExportHref, type GovDecisionRow } from '../../lib/trends';
import { EmptyState, Panel, ShareBar, Sparkline, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { ExportLink } from '../fleet/parts';
import { SectionError, useClientSort, type SectionProps } from './parts';

const declined = (row: GovDecisionRow) => row.cancelled + row.rejected + row.expired;

function share(row: GovDecisionRow, part: number, what: string): ReactNode {
  return (
    <span className="ui-cell-stack">
      <ShareBar value={shareOf(part, row.total)} label={`${row.app}: ${what}, ${count(part)} of ${count(row.total)}`} />
      <span className="ui-cell-sub">{count(part)}</span>
    </span>
  );
}

export function TrendsDecisions({ query, filter, onShowRows, onApps }: SectionProps & { onApps?: (apps: readonly string[]) => void }) {
  const { load, reload, reloading } = useLoad(() => trendsApi.decisions(query), `decisions|${JSON.stringify(query)}`);
  const answer = load.state === 'ready' ? load.data : null;
  const appKey = answer ? answer.items.map((row) => row.app).join('\n') : '';
  useEffect(() => {
    if (onApps && appKey) onApps(appKey.split('\n'));
  }, [appKey, onApps]);
  const { rows, sort } = useClientSort(answer?.items ?? [], (row, key) => {
    switch (key) {
      case 'app': return row.app;
      case 'autoRun': return row.autoRun;
      case 'confirmed': return row.confirmed;
      case 'approved': return row.approved;
      case 'declined': return declined(row);
      default: return row.total;
    }
  }, ['app']);

  const open = (row: GovDecisionRow) => {
    if (!answer) return;
    onShowRows({ title: `Decisions · ${row.app}`, choices: [ACTION_CHOICE, AUTO_RUN_CHOICE], app: row.app, range: { from: answer.range.from, to: answer.range.to } });
  };

  const columns: Array<Column<GovDecisionRow>> = [
    { key: 'app', header: 'App', sortable: true, cell: (row) => <Tag>{row.app}</Tag> },
    { key: 'autoRun', header: 'Auto-run', sortable: true, cell: (row) => share(row, row.autoRun, 'ran on its own') },
    { key: 'confirmed', header: 'Confirmed by the person', sortable: true, cell: (row) => share(row, row.confirmed, 'confirmed by the person') },
    { key: 'approved', header: 'Approved by an approver', sortable: true, cell: (row) => share(row, row.approved, 'approved by an approver') },
    {
      key: 'declined', header: 'Declined', sortable: true,
      cell: (row) => (
        <span className="ui-cell-stack">
          <ShareBar value={shareOf(declined(row), row.total)} label={`${row.app}: declined, ${count(declined(row))} of ${count(row.total)}`} />
          <span className="ui-cell-sub">{`${count(row.cancelled)} cancelled · ${count(row.rejected)} rejected · ${count(row.expired)} expired`}</span>
        </span>
      ),
    },
    {
      key: 'total', header: 'Total', numeric: true, sortable: true,
      cell: (row) => <span className="ui-cell-stack"><span>{count(row.total)}</span>{row.undecidedToday > 0 ? <span className="ui-cell-sub">{`${count(row.undecidedToday)} today: no decision yet or expired`}</span> : null}</span>,
    },
    { key: 'spark', header: 'Auto-run share', hideOnPhone: true, cell: (row) => <Sparkline values={sparkValues(row.autoRunSpark)} max={100} label={`${row.app}: auto-run share per day`} /> },
  ];

  return (
    <Panel flush title="Decisions" description="How each app's writes were decided; a row opens its audit rows" actions={<ExportLink href={trendsExportHref('decisions', filter)} />}>
      {load.state === 'failed' ? <div className="ui-panel-pad"><SectionError title="The decisions could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></div> : (
        <Table
          label="Decisions"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.app}
          loading={load.state === 'loading'}
          skeletonRows={4}
          onRowClick={open}
          sort={sort}
          empty={<EmptyState compact icon={ListChecks} title="No decisions in this range">Each app's writes appear here as Gyro proposes and runs them.</EmptyState>}
        />
      )}
    </Panel>
  );
}
